/**
 * Wheelify Cart Validation
 * Prevents multiple additions of free gift products
 */

(function() {
  'use strict';

  let cartCache = null;
  let cartCacheTime = 0;
  const CART_CACHE_DURATION = 5000; // 5 seconds

  /**
   * Get current cart with caching
   */
  async function getCart() {
    const now = Date.now();
    if (cartCache && (now - cartCacheTime) < CART_CACHE_DURATION) {
      return cartCache;
    }

    try {
      const response = await fetch(theme.routes.cart + '?t=' + now, {
        credentials: 'same-origin',
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      
      const cart = await response.json();
      cartCache = cart;
      cartCacheTime = now;
      return cart;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Check if product is already in cart
   */
  async function isProductInCart(productHandle, variantId) {
    console.log('Wheelify Cart Validation: Checking if product is in cart', { productHandle, variantId });
    const cart = await getCart();
    if (!cart || !cart.items || cart.items.length === 0) {
      console.log('Wheelify Cart Validation: Cart is empty');
      return false;
    }

    console.log('Wheelify Cart Validation: Cart items:', cart.items.map(item => ({
      handle: item.product_handle || item.handle,
      variant_id: item.variant_id || item.id,
      title: item.product_title
    })));

    // Check if product handle matches
    const productInCart = cart.items.some(item => {
      const itemHandle = item.product_handle || item.handle || '';
      const itemVariantId = item.variant_id || item.id;
      
      console.log('Wheelify Cart Validation: Comparing', {
        itemHandle,
        productHandle,
        match: itemHandle === productHandle
      });
      
      // Check by handle first
      if (itemHandle === productHandle) {
        console.log('Wheelify Cart Validation: Product found in cart by handle');
        return true;
      }
      
      // Also check by variant ID if provided
      if (variantId && itemVariantId && parseInt(itemVariantId) === parseInt(variantId)) {
        console.log('Wheelify Cart Validation: Product found in cart by variant ID');
        return true;
      }
      
      return false;
    });

    console.log('Wheelify Cart Validation: Product in cart?', productInCart);
    return productInCart;
  }

  /**
   * Show error message
   */
  function showErrorMessage(message, form) {
    // Remove existing error messages
    const existingErrors = form.querySelectorAll('.wheelify-cart-error');
    existingErrors.forEach(error => error.remove());

    const errorDiv = document.createElement('div');
    errorDiv.className = 'wheelify-cart-error';
    errorDiv.style.cssText = 'background-color: #fee; border: 2px solid #fcc; border-radius: 8px; padding: 15px; margin: 15px 0; color: #c33;';
    errorDiv.textContent = message;

    // Insert before the form or add to cart button
    const addToCartBtn = form.querySelector('[data-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.parentElement.insertBefore(errorDiv, addToCartBtn);
    } else {
      form.insertBefore(errorDiv, form.firstChild);
    }

    // Scroll to error
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Disable add to cart button
   */
  function disableAddToCartButton(form, message) {
    const addToCartBtn = form.querySelector('[data-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      const textSpan = addToCartBtn.querySelector('[data-add-to-cart-text]');
      if (textSpan) {
        textSpan.textContent = message || 'Already in Cart';
      }
      addToCartBtn.style.opacity = '0.6';
      addToCartBtn.style.cursor = 'not-allowed';
    }
  }

  /**
   * Check if current page is a Wheelify gift page
   */
  function isWheelifyGiftPage() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('wheelify_gift');
  }

  /**
   * Check if product is a Wheelify gift product
   */
  function isWheelifyGiftProduct() {
    // Check URL parameter
    if (isWheelifyGiftPage()) {
      return true;
    }
    
    // Check for hidden script element with wheelify product marker
    const wheelifyMarker = document.querySelector('script[data-wheelify-product="true"]');
    if (wheelifyMarker) {
      return true;
    }
    
    // Check form for hidden input
    const form = document.querySelector('form.product-single__form, form[action*="/cart/add"]');
    if (form) {
      const wheelifyInput = form.querySelector('input[name="properties[_wheelify_gift]"]');
      if (wheelifyInput && wheelifyInput.value === 'true') {
        return true;
      }
    }
    
    // Check product section data attribute (if added to template)
    const productSection = document.querySelector('[data-product-handle][data-wheelify-gift]');
    if (productSection && productSection.dataset.wheelifyGift === 'true') {
      return true;
    }
    
    return false;
  }

  /**
   * Get product handle from page
   */
  function getProductHandle() {
    // Try to get from product section data attribute
    const productSection = document.querySelector('[data-product-handle]');
    if (productSection) {
      return productSection.dataset.productHandle;
    }

    // Try to get from URL
    const urlMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }

    return null;
  }

  /**
   * Get variant ID from form
   */
  function getVariantId(form) {
    const variantSelect = form.querySelector('[data-product-select]');
    if (variantSelect) {
      return variantSelect.value;
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      return variantInput.value;
    }

    return null;
  }

  /**
   * Validate cart before adding product
   */
  async function validateCartBeforeAdd(event) {
    const form = event.target.closest('form');
    if (!form) {
      return true; // Allow normal flow if no form found
    }

    // Check if this is a Wheelify gift product
    const isWheelifyProduct = isWheelifyGiftProduct();
    if (!isWheelifyProduct) {
      return true; // Allow normal flow for non-Wheelify products
    }

    // Double-check with hidden input
    const wheelifyInput = form.querySelector('input[name="properties[_wheelify_gift]"]');
    const wheelifyMarker = form.querySelector('script[data-wheelify-product="true"]');
    
    if ((!wheelifyInput || wheelifyInput.value !== 'true') && !wheelifyMarker) {
      return true; // Not a Wheelify gift product
    }

    event.preventDefault();
    event.stopPropagation();

    const productHandle = getProductHandle();
    const variantId = getVariantId(form);

    if (!productHandle) {
      console.error('Wheelify: Could not determine product handle');
      return false;
    }

    // Check if product is already in cart
    const inCart = await isProductInCart(productHandle, variantId);

    // Only block if it's a Wheelify product AND already in cart (AND condition)
    if (inCart && isWheelifyProduct) {
      showErrorMessage('This free gift product can only be added once per customer. It appears you already have this item in your cart.', form);
      disableAddToCartButton(form, 'Already in Cart');
      return false;
    }

    // Product not in cart OR not a Wheelify product, allow the form submission
    // Remove event listener temporarily to allow submission
    form.removeEventListener('submit', validateCartBeforeAdd);
    form.submit();
    return true;
  }

  /**
   * Check cart on page load and disable button if needed
   */
  async function checkCartOnLoad() {
    // Check all product forms on the page
    const forms = document.querySelectorAll('form[action*="/cart/add"], form.product-single__form');
    
    for (const form of forms) {
      // First check if it's a Wheelify gift product
      const isWheelifyProduct = isWheelifyGiftProduct();
      
      // Only check cart if it's a Wheelify product
      if (!isWheelifyProduct) {
        continue; // Not a Wheelify gift product, skip validation
      }

      // Double-check with hidden input if available
      const wheelifyInput = form.querySelector('input[name="properties[_wheelify_gift]"]');
      if (!wheelifyInput || wheelifyInput.value !== 'true') {
        // Still check if form has the script marker
        const wheelifyMarker = form.querySelector('script[data-wheelify-product="true"]');
        if (!wheelifyMarker) {
          continue; // Not a Wheelify gift product
        }
      }

      const productHandle = getProductHandle() || form.dataset.productHandle;
      if (!productHandle) {
        continue;
      }

      // Check if product is already in cart (AND condition)
      const inCart = await isProductInCart(productHandle, null);
      
      // Only disable if it's a Wheelify product AND already in cart
      if (inCart && isWheelifyProduct) {
        disableAddToCartButton(form, 'Already in Cart');
        showErrorMessage('This free gift product is already in your cart. You can only add it once per customer.', form);
      }
    }
  }

  /**
   * Intercept fetch calls to cart/add.js
   */
  function interceptCartAddFetch() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      const options = args[1] || {};
      
      // Check if this is a cart add request
      if (typeof url === 'string' && url.includes('/cart/add.js') && 
          (options.method === 'POST' || options.method === undefined)) {
        
        console.log('Wheelify Cart Validation: Intercepted cart add request');
        
        // Check if this is a Wheelify gift product
        const isWheelifyProduct = isWheelifyGiftProduct();
        if (isWheelifyProduct) {
          const form = document.querySelector('form[action*="/cart/add"]');
          if (form) {
            const wheelifyInput = form.querySelector('input[name="properties[_wheelify_gift]"]');
            const wheelifyMarker = form.querySelector('script[data-wheelify-product="true"]');
            const isWheelifyProductForm = (wheelifyInput && wheelifyInput.value === 'true') || 
                                          (form.dataset.wheelifyGift === 'true') ||
                                          wheelifyMarker;
            
            if (isWheelifyProductForm) {
              const productHandle = getProductHandle();
              
              if (productHandle) {
                console.log('Wheelify Cart Validation: Checking if product is in cart:', productHandle);
                
                // Check cart before allowing the fetch (AND condition)
                return isProductInCart(productHandle, null).then(inCart => {
                  // Only block if Wheelify product AND in cart
                  if (inCart && isWheelifyProduct) {
                    console.log('Wheelify Cart Validation: Product already in cart, blocking add');
                    showErrorMessage('This free gift product can only be added once per customer. It appears you already have this item in your cart.', form);
                    disableAddToCartButton(form, 'Already in Cart');
                    
                    // Return a rejected promise to prevent the add
                    return Promise.reject({
                      status: 422,
                      statusText: 'Unprocessable Entity',
                      description: 'This free gift product can only be added once per customer.'
                    });
                  } else {
                    console.log('Wheelify Cart Validation: Product not in cart, allowing add');
                    // Clear cache before allowing add
                    cartCache = null;
                    cartCacheTime = 0;
                    // Allow the fetch to proceed
                    return originalFetch.apply(this, args);
                  }
                });
              }
            }
          }
        }
      }
      
      // For all other requests, use original fetch
      return originalFetch.apply(this, args);
    };
  }

  /**
   * Initialize cart validation
   */
  function initCartValidation() {
    console.log('Wheelify Cart Validation: Initializing...');
    
    // Intercept fetch calls (most important for AJAX adds)
    interceptCartAddFetch();
    
    // NOTE: Form submit handler removed to prevent double submissions
    // We rely on:
    // 1. Button click handler - quick cache check (synchronous)
    // 2. Fetch interceptor - final validation before AJAX cart add (primary)
    // Most Shopify themes use AJAX for add-to-cart, so fetch interceptor handles all validation

    // Intercept add to cart button clicks
    // NOTE: We primarily rely on fetch interceptor for validation to avoid double submissions
    // This click handler provides early blocking if product already in cart (synchronous check via cache)
    document.addEventListener('click', function(event) {
      const addToCartBtn = event.target.closest('[data-add-to-cart]');
      if (!addToCartBtn) return;
      
      const form = addToCartBtn.closest('form');
      if (!form) return;
      
      // Check if it's a Wheelify gift product
      const isWheelifyProduct = isWheelifyGiftProduct();
      
      // Only validate if it's a Wheelify product
      if (!isWheelifyProduct) {
        return; // Not a Wheelify product, allow normal flow
      }
      
      // Double-check with hidden input or marker
      const wheelifyInput = form.querySelector('input[name="properties[_wheelify_gift]"]');
      const wheelifyMarker = form.querySelector('script[data-wheelify-product="true"]');
      
      if ((!wheelifyInput || wheelifyInput.value !== 'true') && !wheelifyMarker) {
        return; // Not a Wheelify gift product
      }
      
      const productHandle = getProductHandle() || form.dataset.productHandle;
      
      // Quick synchronous check using cached cart data if available
      if (productHandle && cartCache && cartCache.items) {
        const inCart = cartCache.items.some(item => {
          const itemHandle = item.product_handle || item.handle || '';
          return itemHandle === productHandle;
        });
        
        if (inCart && isWheelifyProduct) {
          console.log('Wheelify Cart Validation: Product already in cart (cached check), blocking');
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          showErrorMessage('This free gift product is already in your cart. You can only add it once per customer.', form);
          disableAddToCartButton(form, 'Already in Cart');
          return; // Block the submission
        }
      }
      
      // If not in cache or not in cart, allow normal flow
      // The fetch interceptor will do the final validation before actual cart add
    }, true); // Use capture phase

    // Check cart on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        checkCartOnLoad();
        // Also check after delays to catch dynamically loaded elements
        setTimeout(checkCartOnLoad, 300);
        setTimeout(checkCartOnLoad, 800);
        setTimeout(checkCartOnLoad, 1500);
      });
    } else {
      checkCartOnLoad();
      // Also check after delays to catch dynamically loaded elements
      setTimeout(checkCartOnLoad, 300);
      setTimeout(checkCartOnLoad, 800);
      setTimeout(checkCartOnLoad, 1500);
    }
  }

  // Initialize immediately (don't wait for DOMContentLoaded)
  // This is critical to intercept fetch before theme.js uses it
  initCartValidation();
  
  // Also initialize on DOMContentLoaded as a fallback
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      // Re-initialize to catch any buttons added after load
      console.log('Wheelify Cart Validation: Re-initializing on DOMContentLoaded');
      initCartValidation();
    });
  }

  // Clear cart cache when items are added and PERMANENTLY disable button
  document.addEventListener('ajaxProduct:added', function(event) {
    cartCache = null;
    cartCacheTime = 0;
    
    // Check if this was a Wheelify product (AND condition)
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      const isWheelifyProduct = isWheelifyGiftProduct();
      if (isWheelifyProduct) {
        const wheelifyInput = form.querySelector('input[name="properties[_wheelify_gift]"]');
        const wheelifyMarker = form.querySelector('script[data-wheelify-product="true"]');
        const isWheelifyProductForm = (wheelifyInput && wheelifyInput.value === 'true') || 
                                      (form.dataset.wheelifyGift === 'true') ||
                                      wheelifyMarker;
        
        // Only disable if it's a Wheelify product AND we can verify it was added
        if (isWheelifyProductForm) {
          const productHandle = getProductHandle() || form.dataset.productHandle;
          
          // Double-check it's in cart before disabling (AND condition)
          if (productHandle) {
            isProductInCart(productHandle, null).then(inCart => {
              if (inCart && isWheelifyProduct) {
                // Permanently disable the button after successful add
                disableAddToCartButton(form, 'Added to Cart');
                
                // Also remove any click handlers to prevent re-enabling
                const addToCartBtn = form.querySelector('[data-add-to-cart]');
                if (addToCartBtn) {
                  // Clone and replace to remove all event listeners
                  const newBtn = addToCartBtn.cloneNode(true);
                  newBtn.disabled = true;
                  addToCartBtn.parentNode.replaceChild(newBtn, addToCartBtn);
                  console.log('Wheelify Cart Validation: Button permanently disabled after add');
                }
              }
            });
          }
        }
      }
    }
  });
  
  // Also listen for successful cart updates
  document.addEventListener('cart:updated', function() {
    const isWheelifyProduct = isWheelifyGiftProduct();
    if (isWheelifyProduct) {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        const wheelifyInput = form.querySelector('input[name="properties[_wheelify_gift]"]');
        const wheelifyMarker = form.querySelector('script[data-wheelify-product="true"]');
        const isWheelifyProductForm = (wheelifyInput && wheelifyInput.value === 'true') || 
                                      (form.dataset.wheelifyGift === 'true') ||
                                      wheelifyMarker;
        
        if (isWheelifyProductForm) {
          const productHandle = getProductHandle() || form.dataset.productHandle;
          if (productHandle) {
            // AND condition: only disable if Wheelify product AND in cart
            isProductInCart(productHandle, null).then(inCart => {
              if (inCart && isWheelifyProduct) {
                disableAddToCartButton(form, 'Added to Cart');
                console.log('Wheelify Cart Validation: Cart updated, verified product in cart');
              }
            });
          }
        }
      }
    }
  });

})();

